import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search, Plus, HelpCircle, Briefcase, Pencil } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useApp } from '@/context/AppContext';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ListGridToggle } from '@/components/shared/ListGridToggle';
import OrgDepartmentCard from '@/components/shared/org/OrgDepartmentCard';
import OrgDepartmentSheet from '@/components/shared/org/OrgDepartmentSheet';
import ImportPositionsDialog from '@/components/client/ImportPositionsDialog';
import ClientDeptPositions from '@/components/client/ClientDeptPositions';

/**
 * UAT Finding 10 + Founder UAT (A) — Client portal "Organizational Workspace".
 * Formal positions use the canonical Phase 3 Position model via
 * /client/positions; the legacy per-department string catalogue only feeds
 * title suggestions inside the management dialog.
 */
export default function ClientDepartments() {
  const { t } = useTranslation();
  const { user } = useApp();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newDept, setNewDept] = useState({ name: '', name_es: '', code: '' });
  const [query, setQuery] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetDept, setSheetDept] = useState(null);
  const [view, setView] = useState('grid');
  const [importOpen, setImportOpen] = useState(false);
  const [positionsDept, setPositionsDept] = useState(null);
  const canManage = user?.role === 'client_admin' || user?.role === 'client_hr';

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/client/departments');
      setDepartments(data || []);
    } catch (e) {
      toast.error(t('pages.clientDepartments.createFailed'), { description: formatApiError(e) });
    } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const onCreate = async () => {
    if (!newDept.name.trim() || !user?.company_id) {
      toast.error(t('pages.clientDepartments.createFailed'));
      return;
    }
    setSaving(true);
    try {
      await api.post('/client/departments', {
        company_id: user.company_id,
        name: newDept.name.trim(),
        name_es: newDept.name_es?.trim() || null,
        code: newDept.code.trim() || newDept.name.trim().slice(0, 4).toUpperCase(),
      });
      toast.success(t('pages.clientDepartments.createdToast'));
      setNewOpen(false);
      setNewDept({ name: '', name_es: '', code: '' });
      reload();
    } catch (e) {
      toast.error(t('pages.clientDepartments.createFailed'), { description: formatApiError(e) });
    } finally { setSaving(false); }
  };

  const openSheet = (dept) => { setSheetDept(dept); setSheetOpen(true); };

  // Founder UAT 2026-06 — "Editar departamento": edits the SAME record via
  // PATCH /client/departments/{id} (audited server-side, no duplicates).
  const [editDept, setEditDept] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: '', name_es: '', code: '', responsible_id: '' });
  const openEdit = (dept) => {
    setEditDraft({
      name: dept.name || '',
      name_es: dept.name_es || '',
      code: dept.code || '',
      responsible_id: dept.responsible_id || '',
    });
    setEditDept(dept);
  };
  const onSaveEdit = async () => {
    if (!editDept || !editDraft.name.trim()) {
      toast.error(t('pages.clientDepartments.editFailed', { defaultValue: 'El nombre del departamento es obligatorio.' }));
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/client/departments/${editDept.id}`, {
        name: editDraft.name.trim(),
        name_es: editDraft.name_es?.trim() || null,
        code: editDraft.code?.trim() || null,
        responsible_id: editDraft.responsible_id || null,
      });
      toast.success(t('pages.clientDepartments.editedToast', { defaultValue: 'Departamento actualizado' }));
      setEditDept(null);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return departments;
    return departments.filter((d) => {
      if ((d.name || '').toLowerCase().includes(q)) return true;
      if ((d.name_es || '').toLowerCase().includes(q)) return true;
      if ((d.code || '').toLowerCase().includes(q)) return true;
      if ((d.members_preview || []).some((m) => (m.name || '').toLowerCase().includes(q))) return true;
      if ((d.positions || []).some((p) => (p || '').toLowerCase().includes(q))) return true;
      return false;
    });
  }, [departments, query]);

  return (
    <>
      <Topbar
        title={t('pages.clientDepartments.title')}
        subtitle={t('pages.clientDepartments.org.pageSubtitle', {
          defaultValue: 'Real-time organizational workspace — members and positions',
        })}
        actions={
          <div className="flex items-center gap-2">
            {canManage && (
              <Button
                size="sm"
                variant="outline"
                data-testid="import-positions-btn"
                onClick={() => setImportOpen(true)}
                className="gap-1.5"
              >
                <Briefcase className="h-4 w-4" />
                {t('pages.clientDepartments.importPositions', { defaultValue: 'Import positions' })}
              </Button>
            )}
            <Button
              size="sm"
              data-testid="create-dept-btn"
              onClick={() => setNewOpen(true)}
              className="bg-gradient-primary hover:opacity-90 gap-1.5"
            >
              <Plus className="h-4 w-4" /> {t('pages.clientDepartments.createDept')}
            </Button>
          </div>
        }
      />
      <div className="p-6 lg:p-8 space-y-5">
        <Card className="p-4 shadow-card flex flex-wrap items-center gap-3" data-testid="dept-toolbar">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-3.5 w-3.5 absolute top-2.5 left-2.5 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('pages.clientDepartments.org.searchToolbarPh', {
                defaultValue: 'Search employees, positions, department names…',
              })}
              className="pl-8 h-9 text-sm"
              data-testid="org-toolbar-search"
            />
          </div>
          <ListGridToggle value={view} onChange={setView} testIdPrefix="dept-view" />
        </Card>

        {loading ? (
          <Card className="p-12 text-center text-sm text-muted-foreground" data-testid="dept-loading">
            {t('common.loading')}
          </Card>
        ) : !filtered.length ? (
          <Card className="p-12 text-center text-sm text-muted-foreground italic" data-testid="dept-empty">
            {query
              ? t('common.noResults', { defaultValue: 'No results' })
              : t('pages.clientDepartments.empty')}
          </Card>
        ) : view === 'grid' ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dept-grid">
            {filtered.map((d) => (
              <div key={d.id} className="group">
                <OrgDepartmentCard
                  department={d}
                  onOpenSheet={openSheet}
                  canManagePositions={canManage}
                  onManagePositions={(dept) => setPositionsDept(dept)}
                  headerAction={canManage ? (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 gap-1 text-xs shrink-0"
                      onClick={(e) => { e.stopPropagation(); openEdit(d); }}
                      data-testid={`dept-edit-btn-${d.id}`}
                      title={t('pages.clientDepartments.edit', { defaultValue: 'Editar departamento' })}
                    >
                      <Pencil className="h-3 w-3" />
                      {t('pages.clientDepartments.editShort', { defaultValue: 'Editar' })}
                    </Button>
                  ) : null}
                  testIdPrefix="dept"
                />
              </div>
            ))}
          </div>
        ) : (
          <Card className="shadow-card overflow-hidden" data-testid="dept-list">
            <Table>
              <TableHeader className="bg-muted/40">
                <TableRow>
                  <TableHead>{t('pages.clientDepartments.list.thName', { defaultValue: 'Department' })}</TableHead>
                  <TableHead>{t('pages.clientDepartments.list.thCode', { defaultValue: 'Code' })}</TableHead>
                  <TableHead className="text-center">{t('pages.clientDepartments.list.thMembers', { defaultValue: 'Members' })}</TableHead>
                  <TableHead className="text-center hidden md:table-cell">
                    <span className="inline-flex items-center gap-1"
                          title={t('pages.clientDepartments.org.positionsTip', { defaultValue: 'Job positions defined for this department. Used to organize roles and suggest evaluation targets.' })}
                          data-testid="dept-list-positions-header">
                      {t('pages.clientDepartments.list.thPositions', { defaultValue: 'Positions' })}
                      <HelpCircle className="h-3 w-3 text-muted-foreground" />
                    </span>
                  </TableHead>
                  <TableHead className="text-right">{t('pages.clientDepartments.list.thAction', { defaultValue: 'Action' })}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((d) => (
                  <TableRow key={d.id} className="cursor-pointer hover:bg-muted/40" onClick={() => openSheet(d)} data-testid={`dept-list-row-${d.id}`}>
                    <TableCell>
                      <p className="font-medium">{d.name}</p>
                      {d.name_es && d.name_es !== d.name && <p className="text-xs text-muted-foreground">{d.name_es}</p>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{d.code || '—'}</TableCell>
                    <TableCell className="text-center tabular-nums font-semibold">
                      {d.member_count ?? (d.members_preview || []).length}
                    </TableCell>
                    <TableCell className="text-center hidden md:table-cell">
                      {(d.positions || []).length > 0 ? (
                        <span className="tabular-nums">{(d.positions || []).length}</span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid={`dept-list-positions-empty-${d.id}`}>
                          <Plus className="h-3 w-3" />
                          {t('pages.clientDepartments.list.noPositions', { defaultValue: 'No positions yet' })}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {canManage && (
                        <Button size="sm" variant="outline" className="mr-1 h-7 px-2 gap-1 text-xs"
                                onClick={(e) => { e.stopPropagation(); openEdit(d); }}
                                data-testid={`dept-list-edit-${d.id}`}>
                          <Pencil className="h-3 w-3" />
                          {t('pages.clientDepartments.editShort', { defaultValue: 'Editar' })}
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" data-testid={`dept-list-open-${d.id}`}>
                        {t('pages.clientDepartments.list.open', { defaultValue: 'View' })}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent data-testid="create-dept-dialog">
          <DialogHeader>
            <DialogTitle>{t('pages.clientDepartments.newTitle')}</DialogTitle>
            <DialogDescription>{t('pages.clientDepartments.newDesc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{t('pages.clientDepartments.name', { defaultValue: 'Name (English)' })}</Label>
              <Input
                value={newDept.name}
                onChange={(e) => setNewDept({ ...newDept, name: e.target.value })}
                placeholder={t('pages.clientDepartments.namePh')}
                data-testid="create-dept-name"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs">{t('departments.nameEs', { defaultValue: 'Name (Spanish, optional)' })}</Label>
              <Input
                value={newDept.name_es}
                onChange={(e) => setNewDept({ ...newDept, name_es: e.target.value })}
                placeholder={t('departments.nameEsPh', { defaultValue: 'Leave blank to fall back to English' })}
                data-testid="create-dept-name-es"
              />
            </div>
            <div>
              <Label className="text-xs">{t('pages.clientDepartments.code')}</Label>
              <Input
                value={newDept.code}
                onChange={(e) => setNewDept({ ...newDept, code: e.target.value })}
                placeholder={t('pages.clientDepartments.codeSamplePh')}
                data-testid="create-dept-code"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>{t('pages.clientDepartments.cancel')}</Button>
            <Button onClick={onCreate} disabled={saving} data-testid="create-dept-save" className="bg-gradient-primary">
              {saving ? t('common.saving') : t('pages.clientDepartments.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Founder UAT 2026-06 — Editar departamento (same record, audited) */}
      <Dialog open={!!editDept} onOpenChange={(o) => { if (!o) setEditDept(null); }}>
        <DialogContent data-testid="edit-dept-dialog">
          <DialogHeader>
            <DialogTitle>{t('pages.clientDepartments.editTitle', { defaultValue: 'Editar departamento' })}</DialogTitle>
            <DialogDescription>
              {t('pages.clientDepartments.editDesc', { defaultValue: 'Los cambios actualizan este mismo departamento. Colaboradores, puestos y registros históricos permanecen vinculados.' })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">{t('pages.clientDepartments.name', { defaultValue: 'Name (English)' })}</Label>
              <Input value={editDraft.name}
                     onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                     data-testid="edit-dept-name" autoFocus />
            </div>
            <div>
              <Label className="text-xs">{t('departments.nameEs', { defaultValue: 'Name (Spanish, optional)' })}</Label>
              <Input value={editDraft.name_es}
                     onChange={(e) => setEditDraft({ ...editDraft, name_es: e.target.value })}
                     data-testid="edit-dept-name-es" />
            </div>
            <div>
              <Label className="text-xs">{t('pages.clientDepartments.code')}</Label>
              <Input value={editDraft.code}
                     onChange={(e) => setEditDraft({ ...editDraft, code: e.target.value })}
                     data-testid="edit-dept-code" />
            </div>
            {(editDept?.members_preview || []).length > 0 && (
              <div>
                <Label className="text-xs">{t('pages.clientDepartments.responsible', { defaultValue: 'Responsable del departamento (opcional)' })}</Label>
                <Select value={editDraft.responsible_id || '__none__'}
                        onValueChange={(v) => setEditDraft({ ...editDraft, responsible_id: v === '__none__' ? '' : v })}>
                  <SelectTrigger data-testid="edit-dept-responsible"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t('pages.clientDepartments.noResponsible', { defaultValue: 'Sin responsable' })}</SelectItem>
                    {(editDept?.members_preview || []).map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDept(null)}>{t('pages.clientDepartments.cancel')}</Button>
            <Button onClick={onSaveEdit} disabled={saving} data-testid="edit-dept-save" className="bg-gradient-primary">
              {saving ? t('common.saving') : t('common.save', { defaultValue: 'Guardar' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OrgDepartmentSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        department={sheetDept}
        apiPrefix="client"
        allDepartments={departments}
        canManageMembers={false}
        onAfterChange={reload}
      />

      <ImportPositionsDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={reload}
      />

      <ClientDeptPositions
        open={!!positionsDept}
        onOpenChange={(v) => { if (!v) setPositionsDept(null); }}
        department={positionsDept}
        canManage={canManage}
        onChanged={reload}
      />
    </>
  );
}
